import { useCallback, useEffect, useState } from "react";
import {
  fetchPublicExperiences,
  fetchPublicFarms,
  fetchPublicFarmers,
  fetchPublicReviews,
  type PublicExperience,
} from "@/lib/publicCatalog";
import type { Farm } from "@/mocks/farms";
import type { FarmerProfile } from "@/mocks/farmerProfiles";
import type { Testimonial } from "@/mocks/testimonials";

export interface PublicResource<T> {
  data: T;
  loading: boolean;
  error: string;
  reload: () => void;
}

function usePublicResource<T>(loader: () => Promise<T>, initial: T): PublicResource<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    setError("");
    loader()
      .then((result) => setData(result))
      .catch(() => setError("We couldn't load this content right now. Please try again."))
      .finally(() => setLoading(false));
  }, [loader]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
}

/** Live experiences (listing + detail shapes) straight from the backend. */
export function usePublicExperiences(): PublicResource<PublicExperience[]> {
  return usePublicResource(fetchPublicExperiences, [] as PublicExperience[]);
}

/** Live farm catalogue from the backend. */
export function usePublicFarms(): PublicResource<Farm[]> {
  return usePublicResource(fetchPublicFarms, [] as Farm[]);
}

/** Live farmer profiles from the backend. */
export function usePublicFarmers(): PublicResource<FarmerProfile[]> {
  return usePublicResource(fetchPublicFarmers, [] as FarmerProfile[]);
}

/** Live published reviews / testimonials from the backend. */
export function usePublicReviews(): PublicResource<Testimonial[]> {
  return usePublicResource(fetchPublicReviews, [] as Testimonial[]);
}